import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Euro, FileText, AlertCircle, TrendingUp, Plus, Bell, BellOff, Users } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Badge } from '../components/ui/badge';
import notificationService from '../services/notificationService';
import { formatCurrency } from '../utils/formatCurrency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsSupported, setNotificationsSupported] = useState(false);

  const statusConfig = {
    draft: { label: t('invoices.statusDraft'), color: 'bg-slate-100 text-slate-600 border-slate-200' },
    sent: { label: t('invoices.statusSent'), color: 'bg-blue-50 text-blue-600 border-blue-200' },
    paid: { label: t('invoices.statusPaid'), color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    overdue: { label: t('invoices.statusOverdue'), color: 'bg-amber-50 text-amber-600 border-amber-200' },
  };

  useEffect(() => {
    fetchDashboardStats();
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    const supported = notificationService.isSupported();
    setNotificationsSupported(supported);
    
    if (supported) {
      const isSubscribed = await notificationService.isSubscribed();
      setNotificationsEnabled(isSubscribed);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      setStats(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotifications = async () => {
    try {
      if (notificationsEnabled) {
        const token = localStorage.getItem('token');
        await notificationService.unsubscribe(token);
        setNotificationsEnabled(false);
        toast.success(t('common.success'));
      } else {
        const permission = await notificationService.requestPermission();
        
        if (permission === 'granted') {
          const token = localStorage.getItem('token');
          await notificationService.subscribe(token);
          setNotificationsEnabled(true);
          toast.success(t('common.success'));
          
          await notificationService.showLocalNotification(
            t('common.success'),
            {
              body: t('dashboard.overdueInvoices'),
              icon: '/logo.png'
            }
          );
        } else {
          toast.error(t('common.error'));
        }
      }
    } catch (error) {
      console.error('Notification error:', error);
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-500">{t('common.loading')}</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-500">{t('common.error')}</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900" data-testid="dashboard-title">
            {t('dashboard.title')}
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          {notificationsSupported && (
            <Button
              variant="outline"
              onClick={handleToggleNotifications}
              data-testid="toggle-notifications-button"
              className={`w-full sm:w-auto rounded-sm ${
                notificationsEnabled 
                  ? 'border-emerald-600 text-emerald-600 hover:bg-emerald-50' 
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {notificationsEnabled ? (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">{t('common.success')}</span>
                  <span className="sm:hidden">{t('common.success')}</span>
                </>
              ) : (
                <>
                  <BellOff className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">{t('common.add')}</span>
                  <span className="sm:hidden">{t('common.add')}</span>
                </>
              )}
            </Button>
          )}
          <Link to="/invoices/create" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto rounded-sm bg-primary hover:bg-[#D62839] shadow-none" data-testid="create-invoice-button">
              <Plus className="w-4 h-4 mr-2" />
              {t('invoices.newInvoice')}
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200 shadow-none rounded-sm" data-testid="revenue-today-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">{t('dashboard.revenueToday')}</CardTitle>
            <Euro className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="font-mono text-2xl font-bold text-slate-900">
              {formatCurrency(stats.revenue_today || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-none rounded-sm" data-testid="revenue-month-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">{t('dashboard.revenueMonth')}</CardTitle>
            <TrendingUp className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="font-mono text-2xl font-bold text-slate-900">
              {formatCurrency(stats.revenue_month || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-none rounded-sm" data-testid="open-invoices-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">{t('dashboard.openInvoices')}</CardTitle>
            <FileText className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="font-mono text-2xl font-bold text-slate-900">
              {stats.open_invoices_count || 0}
            </div>
            <div className="font-mono text-sm text-slate-500 mt-1">
              {formatCurrency(stats.open_invoices_total || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-none rounded-sm" data-testid="overdue-invoices-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">{t('dashboard.overdueInvoices')}</CardTitle>
            <AlertCircle className="w-4 h-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="font-mono text-2xl font-bold text-amber-600">
              {stats.overdue_invoices_count || 0}
            </div>
            <div className="font-mono text-sm text-amber-500 mt-1">
              {formatCurrency(stats.overdue_invoices_total || 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart and Recent Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card className="border-slate-200 shadow-none rounded-sm" data-testid="revenue-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">{t('dashboard.monthlyRevenue')}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.monthly_revenue && stats.monthly_revenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.monthly_revenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '0.125rem',
                      fontFamily: 'JetBrains Mono'
                    }}
                    formatter={(value) => [formatCurrency(value), t('dashboard.monthlyRevenue')]}
                  />
                  <Bar dataKey="revenue" fill="#E63946" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                {t('dashboard.noData')}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Invoices */}
        <Card className="border-slate-200 shadow-none rounded-sm" data-testid="recent-invoices">
          <CardHeader>
            <CardTitle className="font-heading text-lg">{t('dashboard.recentInvoices')}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recent_invoices && stats.recent_invoices.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_invoices.map((invoice) => (
                  <Link
                    key={invoice.id}
                    to={`/invoices`}
                    className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-sm border border-slate-200 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-mono text-sm font-medium text-slate-900">
                        #{invoice.invoice_number}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {formatCurrency(invoice.total_amount)}
                      </p>
                    </div>
                    <Badge className={`rounded-full ${statusConfig[invoice.status]?.color} border`}>
                      {statusConfig[invoice.status]?.label}
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                {t('invoices.noInvoices')}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Year Revenue */}
      <Card className="border-slate-200 shadow-none rounded-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg">{t('dashboard.revenueYear')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="font-mono text-4xl font-bold text-primary">
            {formatCurrency(stats.revenue_year || 0)}
          </div>
          <p className="text-sm text-slate-500 mt-2">{t('dashboard.revenueYear')}</p>
        </CardContent>
      </Card>

      {/* Revenue by Customer */}
      <Card className="border-slate-200 shadow-none rounded-sm" data-testid="revenue-by-customer">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-400" />
            <CardTitle className="font-heading text-lg">{t('dashboard.revenueByCustomer')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {stats.revenue_by_customer && stats.revenue_by_customer.length > 0 ? (
            <div className="space-y-4">
              {/* Bar Chart */}
              <ResponsiveContainer width="100%" height={Math.min(stats.revenue_by_customer.length * 50 + 40, 300)}>
                <BarChart 
                  data={stats.revenue_by_customer.slice(0, 10)} 
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#64748b" style={{ fontSize: '11px' }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                  <YAxis 
                    type="category" 
                    dataKey="customer_name" 
                    stroke="#64748b" 
                    style={{ fontSize: '11px' }} 
                    width={120}
                    tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '0.125rem',
                      fontFamily: 'JetBrains Mono'
                    }}
                    formatter={(value) => [formatCurrency(value), t('dashboard.revenueByCustomer')]}
                  />
                  <Bar dataKey="total_revenue" fill="#E63946" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>

              {/* Customer List */}
              <div className="border-t border-slate-200 pt-4 space-y-2">
                {stats.revenue_by_customer.map((customer, index) => (
                  <div 
                    key={customer.customer_id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-100 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 bg-primary text-white text-xs font-bold rounded-full">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm text-slate-900">{customer.customer_name}</p>
                        <p className="text-xs text-slate-500">
                          {customer.invoice_count} {customer.invoice_count === 1 ? t('invoices.invoiceCount', { count: 1 }) : t('invoices.invoiceCount_plural', { count: customer.invoice_count })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm font-bold text-slate-900">{formatCurrency(customer.total_revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-slate-400">
              {t('dashboard.noData')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;